import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ExternalLink, CheckCircle, AlertCircle, Loader, ArrowLeft } from 'lucide-react';

const TrustpilotSettings = ({ api, onBack }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [addingDomain, setAddingDomain] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await api.getBusinessLocations();
      setLocations(response.locations || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDomain = async (domain) => {
    if (!domain.trim()) {
      alert('Please enter a domain');
      return;
    }

    try {
      setVerifying(true);
      setVerifyResult(null);

      const result = await api.verifyTrustpilotDomain(domain);
      
      if (result.success) {
        setVerifyResult({
          success: true,
          domain: result.domain,
          companyInfo: result.companyInfo
        });
      }
    } catch (error) {
      setVerifyResult({
        success: false,
        error: error.message || 'Could not verify domain'
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleAddDomain = async (locationId) => {
    if (!newDomain.trim()) {
      alert('Please enter a Trustpilot domain');
      return;
    }

    try {
      setAddingDomain(locationId);
      
      const result = await api.addTrustpilotDomain(locationId, newDomain);
      
      if (result.success) {
        await loadLocations();
        setNewDomain('');
        setVerifyResult(null);
        alert('Trustpilot domain added successfully!');
      }
    } catch (error) {
      alert('Failed to add domain: ' + error.message);
    } finally {
      setAddingDomain(null);
    }
  };

  const handleRemoveDomain = async (locationId, locationName) => {
    if (!window.confirm(`Remove Trustpilot domain from "${locationName}"?`)) {
      return;
    }

    try {
      await api.removeTrustpilotDomain(locationId);
      await loadLocations();
      alert('Trustpilot domain removed');
    } catch (error) {
      alert('Failed to remove domain: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b px-6 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reviews
          </button>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Reviews
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Trustpilot Integration</h2>
          <p className="text-gray-600 mt-2">
            Add your Trustpilot domain to each business location to monitor reviews
          </p>
        </div>

        {/* Domain Verification Helper */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Find Your Trustpilot Domain</h3>
          <ol className="text-sm text-blue-800 space-y-1 mb-3">
            <li>1. Go to your Trustpilot page (e.g., trustpilot.com/review/yourcompany.com)</li>
            <li>2. Copy just the domain part after "/review/" (e.g., "yourcompany.com")</li>
            <li>3. Enter it below to verify before adding</li>
          </ol>
          
          <div className="flex gap-2">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => {
                setNewDomain(e.target.value);
                setVerifyResult(null);
              }}
              placeholder="e.g., yourcompany.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <button
              onClick={() => handleVerifyDomain(newDomain)}
              disabled={verifying || !newDomain.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {verifying ? 'Verifying...' : 'Verify'}
            </button>
          </div>

          {verifyResult && (
            <div className={`mt-3 p-3 rounded-md ${
              verifyResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {verifyResult.success ? (
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-900">
                      Found: {verifyResult.companyInfo.companyName}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Trust Score: {verifyResult.companyInfo.trustScore}/5 • 
                      {' '}{verifyResult.companyInfo.totalReviews} reviews
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-900">{verifyResult.error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Business Locations */}
        <div className="space-y-4">
          {locations.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-600 mb-4">No business locations yet</p>
              <p className="text-sm text-gray-500">
                Create a business location in the Reviews section first
              </p>
            </div>
          ) : (
            locations.map((location) => (
              <div
                key={location.id}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{location.name}</h3>
                    {location.address && (
                      <p className="text-sm text-gray-600">{location.address}</p>
                    )}
                    
                    {location.trustpilotBusinessUnitId ? (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Connected
                        </span>
                        <a
                          href={`https://www.trustpilot.com/review/${location.trustpilotBusinessUnitId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center"
                        >
                          {location.trustpilotBusinessUnitId}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mt-2">No Trustpilot domain configured</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {location.trustpilotBusinessUnitId ? (
                      <button
                        onClick={() => handleRemoveDomain(location.id, location.name)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Remove Trustpilot domain"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddDomain(location.id)}
                        disabled={!verifyResult?.success || addingDomain === location.id}
                        className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {addingDomain === location.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Add Domain
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">How it works</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Add your Trustpilot domain to each location you want to monitor</li>
            <li>• Reviews are automatically synced when you click "Sync Now" in the Reviews dashboard</li>
            <li>• Urgent reviews (2 stars or less) trigger notifications</li>
            <li>• Click "Respond on Trustpilot" to reply directly on the platform</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TrustpilotSettings;